// Ported from monorepo corpus/recommend/page.tsx — a server component there,
// a client fetch against POST /api/recommend here. The empty vocabulary
// profile is preserved deliberately (the source's documented placeholder;
// wiring a real profile from the assessment system stays a product TODO),
// and the legacy ALLOWED_PUBLICATION_PATTERNS ride along as this consumer's
// publication scope — a per-request filter now, not a store rule (§2).
import { useEffect, useState } from "react";
import type { ReadingCandidate } from "@bindery/shared/corpus/selection";
import { recommend } from "../lib/api";
import { useTokenSession } from "@bindery/auth-consumer/react";
import { SessionGate } from "@bindery/ui/components/session/session-gate";
import { RecommendationList } from "../components/recommendation-list";
import { signIn } from "../lib/auth";

/** The curated series this consumer scopes recommendations to (§2). */
const PUBLICATION_SCOPE = { titlePatterns: ["500 %", "Poetry %"] };

type ListState =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; candidates: ReadingCandidate[] };

export function RecommendPage() {
  const tokenState = useTokenSession();
  const [list, setList] = useState<ListState>({ kind: "loading" });

  useEffect(() => {
    if (tokenState.kind !== "ready") return;
    let cancelled = false;
    void recommend(tokenState.token, {
      assessedWordIds: [], // from reader assessment — TODO, per the source
      predictedWordIds: [], // from the vocabulary model
      predictedConfidences: [],
      extraKnownWords: [],
      options: { limit: 20, publicationScope: PUBLICATION_SCOPE },
    }).then((result) => {
      if (cancelled) return;
      setList(
        result.kind === "ok"
          ? { kind: "ready", candidates: result.data }
          : { kind: "error" },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [tokenState]);

  return (
    <main className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6">
        <p className="catalog-label mb-2">corpus · recommender</p>
        <h1 className="font-serif text-3xl font-bold">
          Reading Recommendations
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Texts matched to your vocabulary profile, sorted by reading challenge
          score.
        </p>
      </div>

      <SessionGate
        status={tokenState.kind}
        onSignIn={() => signIn()}
        signedOutMessage="Recommendations are available to signed-in readers only."
        loading={<MatchingTexts />}
      >
        {list.kind === "loading" ? (
          <MatchingTexts />
        ) : list.kind === "error" ? (
          <div className="bg-muted/40 flex min-h-48 items-center justify-center rounded-lg border border-dashed">
            <p className="text-muted-foreground text-sm">
              The recommender is unavailable right now. Try again shortly.
            </p>
          </div>
        ) : (
          <RecommendationList candidates={list.candidates} />
        )}
      </SessionGate>
    </main>
  );
}

function MatchingTexts() {
  return (
    <div className="flex min-h-48 items-center justify-center">
      <p className="text-muted-foreground text-sm">
        Matching texts to your vocabulary…
      </p>
    </div>
  );
}
